import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./InvestoContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const freqToMonths = { monthly: 1, quarterly: 3, yearly: 12 };

function toNum(v, fallback = 0) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

const StockPortfolioCalculator = () => {
  const content = article.find((item) => item.id === 2);
  const [mode, setMode] = useState("growth");
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [riskLevel, setRiskLevel] = useState("moderate");
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    initialValue: "10000.00",
    contribution: "500.00",
    contributionFreq: "monthly",
    durationYears: "10",
    expectedReturn: "10.00",
    compounding: "monthly",
    allocationStocks: "60.00",
    allocationETFs: "20.00",
    allocationBonds: "15.00",
    allocationCash: "5.00",
    allocationGold: "0.00",
    allocationRealEstate: "0.00",
    allocationCrypto: "0.00",
    dividendYield: "1.50",
    reinvestDividends: true,
    inflation: "3.00",
    expenseRatio: "0.50",
    taxOnGains: "15.00",
    volatility: "15.00",
    conversionRate: "1.00",
    rebalancing: "yearly",
    goalAmount: "100000.00",
    maxDrawdown: "20.00",
    sharpeRatio: "0.80",
    beta: "1.00",
    correlation: "0.70",
    marketCrash: "30.00",
    recoveryPeriod: "24",
    emergencyFund: "6",
    monthlyExpenses: "3000.00",
    riskTolerance: "moderate",
    portfolioType: "balanced",
    withdrawalRate: "4.00",
    retirementAge: "65",
    currentAge: "35"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculatePortfolio();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value 
    }));
  };

  const calculatePortfolio = () => {
    const {
      initialValue,
      contribution,
      contributionFreq,
      durationYears,
      expectedReturn,
      compounding,
      dividendYield,
      reinvestDividends,
      inflation,
      expenseRatio,
      taxOnGains,
      volatility,
      conversionRate,
      allocationStocks,
      allocationETFs,
      allocationBonds,
      allocationCash,
      allocationGold,
      allocationRealEstate,
      allocationCrypto,
      rebalancing,
      goalAmount,
      maxDrawdown,
      marketCrash,
      recoveryPeriod
    } = inputs;

    const years = Math.max(1, Math.round(toNum(durationYears, 1)));
    const months = years * 12;
    const contribFreqMonths = freqToMonths[contributionFreq] || 1;
    const contributionAmt = toNum(contribution, 0);
    const lump = toNum(initialValue, 0);
    const annualNominal = toNum(expectedReturn, 0) / 100;
    const expense = toNum(expenseRatio, 0) / 100;
    const netAnnual = Math.max(-0.9999, annualNominal - expense);
    const dividend = toNum(dividendYield, 0) / 100;
    const tax = toNum(taxOnGains, 0) / 100;
    const conv = toNum(conversionRate, 1);
    const vol = Math.max(0, toNum(volatility, 0)) / 100;
    const infl = toNum(inflation, 0) / 100;
    const maxDD = toNum(maxDrawdown, 0) / 100;
    const crash = toNum(marketCrash, 0) / 100;
    const recovery = toNum(recoveryPeriod, 24);

    const monthlyRate = Math.pow(1 + netAnnual, 1 / 12) - 1;

    // Arrays for timeline
    const timeline = [];
    const balanceWorst = [];
    const balanceBest = [];
    const investedSeries = [];
    const dividendSeries = [];
    const crashScenario = [];

    let balance = lump;
    let invested = lump;
    let totalDividends = 0;
    let bWorst = lump;
    let bBest = lump;
    let bCrash = lump;

    // Asset allocation tracking
    const allocation = {
      stocks: toNum(allocationStocks, 0) / 100,
      etfs: toNum(allocationETFs, 0) / 100,
      bonds: toNum(allocationBonds, 0) / 100,
      cash: toNum(allocationCash, 0) / 100,
      gold: toNum(allocationGold, 0) / 100,
      realEstate: toNum(allocationRealEstate, 0) / 100,
      crypto: toNum(allocationCrypto, 0) / 100
    };

    // Simulate market crash
    let crashOccurred = false;
    let crashMonth = Math.floor(months * 0.3); // Crash at 30% of timeline

    for (let m = 0; m <= months; m++) {
      if (m === 0) {
        timeline.push({ month: m, value: balance });
        balanceWorst.push(bWorst);
        balanceBest.push(bBest);
        crashScenario.push(bCrash);
        investedSeries.push(invested);
        dividendSeries.push(0);
        continue;
      }

      // Add contribution
      if ((m - 1) % contribFreqMonths === 0) {
        balance += contributionAmt;
        invested += contributionAmt;
        bWorst += contributionAmt;
        bBest += contributionAmt;
        bCrash += contributionAmt;
      }

      // Handle market crash scenario
      if (m === crashMonth && !crashOccurred) {
        bCrash *= (1 - crash);
        crashOccurred = true;
      }

      // Recovery after crash
      if (crashOccurred && m > crashMonth && m <= crashMonth + recovery) {
        const recoveryRate = Math.pow(1 + monthlyRate * 1.5, 1); // Faster recovery
        bCrash *= recoveryRate;
      }

      // Monthly dividend calculation
      const monthlyDividend = balance * (dividend / 12);
      const dividendAfterTax = monthlyDividend * (1 - tax);

      if (reinvestDividends) {
        balance += dividendAfterTax;
        totalDividends += dividendAfterTax;
      } else {
        totalDividends += dividendAfterTax;
      }

      // Apply monthly growth
      balance *= 1 + monthlyRate;
      bBest *= 1 + monthlyRate * (1 + vol);
      bWorst *= 1 + monthlyRate * (1 - vol);
      
      if (!crashOccurred || m > crashMonth + recovery) {
        bCrash *= 1 + monthlyRate;
      }

      // Rebalancing logic
      if (rebalancing !== "none") {
        const rebalanceFreq = rebalancing === "quarterly" ? 3 : 12;
        if (m % rebalanceFreq === 0) {
          // Simplified rebalancing - in real implementation, this would adjust individual asset classes
          // For now, we'll just track that rebalancing occurred
        }
      }

      timeline.push({ month: m, value: balance });
      balanceWorst.push(bWorst);
      balanceBest.push(bBest);
      crashScenario.push(bCrash);
      investedSeries.push(invested);
      dividendSeries.push(totalDividends);
    }

    const finalGross = timeline[timeline.length - 1].value;
    const totalInvested = invested;
    const gainGross = finalGross - totalInvested;
    const finalAfterTax = finalGross - Math.max(0, gainGross) * tax;
    const finalConverted = finalAfterTax * conv;
    const inflationAdjusted = finalConverted / Math.pow(1 + infl, years);

    // Performance metrics
    let cagr = 0;
    if (totalInvested > 0) {
      cagr = Math.pow(finalAfterTax / totalInvested, 1 / years) - 1;
    }

    // Risk metrics
    const maxPortfolioValue = Math.max(...timeline.map(t => t.value));
    const minPortfolioValue = Math.min(...timeline.map(t => t.value));
    const maxDrawdownActual = (maxPortfolioValue - minPortfolioValue) / maxPortfolioValue * 100;

    // Sharpe ratio approximation
    const sharpeRatio = cagr / (vol * Math.sqrt(12));

    // Goal progress
    const goal = toNum(goalAmount, 0);
    const goalPct = goal > 0 ? (finalConverted / goal) * 100 : null;

    // Retirement calculations
    const currentAge = toNum(inputs.currentAge, 35);
    const retirementAge = toNum(inputs.retirementAge, 65);
    const yearsToRetirement = retirementAge - currentAge;
    const monthlyRetirementIncome = finalConverted * (toNum(inputs.withdrawalRate, 0) / 100) / 12;

    const resultData = {
      totalInvested,
      finalValue: finalConverted,
      gain: finalConverted - totalInvested * conv,
      cagr: cagr * 100,
      inflationAdjusted,
      goalPct,
      years,
      timeline,
      balanceWorst,
      balanceBest,
      crashScenario,
      investedSeries,
      dividendSeries,
      totalDividends: totalDividends * conv,
      maxDrawdown: maxDrawdownActual,
      sharpeRatio,
      monthlyRetirementIncome,
      yearsToRetirement,
      allocation,
      portfolioValue: finalConverted
    };

    setResult(resultData);
  };

  const calculateRiskMetrics = () => {
    const { volatility, expectedReturn, allocationStocks, allocationBonds, correlation } = inputs;
    
    const vol = toNum(volatility, 0) / 100;
    const expected = toNum(expectedReturn, 0) / 100;
    const stocks = toNum(allocationStocks, 0) / 100;
    const bonds = toNum(allocationBonds, 0) / 100;
    const corr = toNum(correlation, 0);

    // Simplified portfolio risk calculation
    const portfolioVol = Math.sqrt(
      Math.pow(stocks * vol, 2) + 
      Math.pow(bonds * (vol * 0.3), 2) + 
      2 * stocks * bonds * vol * (vol * 0.3) * corr
    );

    const sharpe = expected / portfolioVol;
    const valueAtRisk = portfolioVol * 1.645; // 95% confidence

    return {
      portfolioVolatility: portfolioVol * 100,
      sharpeRatio: sharpe,
      valueAtRisk: valueAtRisk * 100,
      riskScore: Math.min(portfolioVol * 100, 100)
    };
  };

  const calculateRetirementReadiness = () => {
    const { currentAge, retirementAge, monthlyExpenses, emergencyFund } = inputs;
    
    const current = toNum(currentAge, 35);
    const retirement = toNum(retirementAge, 65);
    const expenses = toNum(monthlyExpenses, 3000);
    const emergency = toNum(emergencyFund, 6);

    const yearsToRetirement = retirement - current;
    const annualExpenses = expenses * 12;
    const retirementCorpus = annualExpenses * 25; // 25x rule
    const emergencyFundRequired = expenses * emergency;

    return {
      yearsToRetirement,
      annualExpenses,
      retirementCorpus,
      emergencyFundRequired,
      readinessScore: Math.min((result?.finalValue || 0) / retirementCorpus * 100, 100)
    };
  };

  const resetCalculator = () => {
    setInputs({
      initialValue: "10000.00",
      contribution: "500.00",
      contributionFreq: "monthly",
      durationYears: "10",
      expectedReturn: "10.00",
      compounding: "monthly",
      allocationStocks: "60.00",
      allocationETFs: "20.00",
      allocationBonds: "15.00",
      allocationCash: "5.00",
      allocationGold: "0.00",
      allocationRealEstate: "0.00",
      allocationCrypto: "0.00",
      dividendYield: "1.50",
      reinvestDividends: true,
      inflation: "3.00",
      expenseRatio: "0.50",
      taxOnGains: "15.00",
      volatility: "15.00",
      conversionRate: "1.00",
      rebalancing: "yearly",
      goalAmount: "100000.00",
      maxDrawdown: "20.00",
      sharpeRatio: "0.80",
      beta: "1.00",
      correlation: "0.70",
      marketCrash: "30.00",
      recoveryPeriod: "24",
      emergencyFund: "6",
      monthlyExpenses: "3000.00",
      riskTolerance: "moderate",
      portfolioType: "balanced",
      withdrawalRate: "4.00",
      retirementAge: "65",
      currentAge: "35"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Total Invested,${result.totalInvested.toFixed(2)}`,
      `Final Portfolio Value,${result.finalValue.toFixed(2)}`,
      `Total Gain,${result.gain.toFixed(2)}`,
      `CAGR,${result.cagr.toFixed(2)}%`,
      `Inflation Adjusted Value,${result.inflationAdjusted.toFixed(2)}`,
      `Max Drawdown,${result.maxDrawdown.toFixed(2)}%`,
      `Sharpe Ratio,${result.sharpeRatio.toFixed(2)}`,
      `Years,${result.years}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'portfolio-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        allocationStocks: "40.00", 
        allocationBonds: "40.00", 
        allocationCash: "20.00",
        expectedReturn: "6.00",
        volatility: "8.00"
      },
      moderate: { 
        allocationStocks: "60.00", 
        allocationBonds: "30.00", 
        allocationCash: "10.00",
        expectedReturn: "8.00",
        volatility: "12.00"
      },
      aggressive: { 
        allocationStocks: "80.00", 
        allocationBonds: "15.00", 
        allocationCash: "5.00",
        expectedReturn: "10.00",
        volatility: "18.00"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} portfolio strategy applied`, "info");
  };

  // Chart Data
  const growthChartData = result ? {
    labels: result.timeline.map((p, i) => `Year ${(i / 12).toFixed(1)}`),
    datasets: [
      {
        label: "Worst Case",
        data: result.balanceWorst.map(v => v),
        borderColor: "#e76f51",
        backgroundColor: "rgba(231,111,81,0.08)",
        fill: true,
        tension: 0.25,
      },
      {
        label: "Expected",
        data: result.timeline.map(p => p.value),
        borderColor: "#28A745",
        backgroundColor: "rgba(40,167,69,0.08)",
        fill: true,
        tension: 0.25,
      },
      {
        label: "Best Case",
        data: result.balanceBest.map(v => v),
        borderColor: "#2a9d8f",
        backgroundColor: "rgba(42,157,143,0.08)",
        fill: true,
        tension: 0.25,
      },
      {
        label: "Crash Scenario",
        data: result.crashScenario.map(v => v),
        borderColor: "#6c757d",
        backgroundColor: "rgba(108,117,125,0.08)",
        borderDash: [5, 5],
        tension: 0.25,
      }
    ]
  } : null;

  const allocationChartData = {
    labels: ["Stocks", "ETFs", "Bonds", "Cash", "Gold", "Real Estate", "Crypto"].filter((_, i) => 
      toNum(inputs[`allocation${["Stocks", "ETFs", "Bonds", "Cash", "Gold", "RealEstate", "Crypto"][i]}`], 0) > 0
    ),
    datasets: [{
      data: [
        toNum(inputs.allocationStocks, 0),
        toNum(inputs.allocationETFs, 0),
        toNum(inputs.allocationBonds, 0),
        toNum(inputs.allocationCash, 0),
        toNum(inputs.allocationGold, 0),
        toNum(inputs.allocationRealEstate, 0),
        toNum(inputs.allocationCrypto, 0)
      ].filter(val => val > 0),
      backgroundColor: ["#19692C", "#2a9d8f", "#f4a261", "#adb5bd", "#ffd700", "#8b4513", "#ff6b6b"],
      borderWidth: 2,
      borderColor: darkMode ? "#1A202C" : "#FFFFFF",
      hoverOffset: 15
    }],
  };

  const riskMetrics = calculateRiskMetrics();
  const retirementReadiness = calculateRetirementReadiness();

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Stock Portfolio Growth Calculator</h1>
        </div>
        <p>Advanced portfolio analysis with risk management, retirement planning, and comprehensive growth projections</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">Portfolio Analysis</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.finalValue >= 0 ? 'positive' : 'negative'}`}>
                        ${calc.finalValue.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Portfolio' : 'Portfolio Calc'}
              </button>
              <button 
                className={`tab ${activeTab === "risk" ? "active" : ""}`}
                onClick={() => setActiveTab("risk")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Risk' : 'Risk Analysis'}
              </button>
              <button 
                className={`tab ${activeTab === "retirement" ? "active" : ""}`}
                onClick={() => setActiveTab("retirement")}
              >
                <i className="fas fa-umbrella-beach"></i>
                {isMobile ? 'Retirement' : 'Retirement Plan'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Portfolio Strategy Presets */}
          <div className="presets-section">
            <h4>Portfolio Strategy:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Portfolio Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Initial Investment</label>
                      <input 
                        type="number" 
                        name="initialValue" 
                        value={inputs.initialValue} 
                        onChange={handleChange}
                        placeholder="10000.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-hand-holding-usd"></i> Regular Contribution</label>
                      <input 
                        type="number" 
                        name="contribution" 
                        value={inputs.contribution} 
                        onChange={handleChange}
                        placeholder="500.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Contribution Frequency</label>
                      <select name="contributionFreq" value={inputs.contributionFreq} onChange={handleChange}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-clock"></i> Investment Duration (Years)</label>
                      <input 
                        type="number" 
                        name="durationYears" 
                        value={inputs.durationYears} 
                        onChange={handleChange}
                        placeholder="10"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Expected Annual Return (%)</label>
                      <input 
                        type="number" 
                        name="expectedReturn" 
                        value={inputs.expectedReturn} 
                        onChange={handleChange}
                        placeholder="10.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-rotate"></i> Compounding Frequency</label>
                      <select name="compounding" value={inputs.compounding} onChange={handleChange}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="halfyearly">Half-Yearly</option>
                        <option value="annually">Annually</option>
                      </select>
                    </div>
                  </div>

                  {/* Asset Allocation */}
                  <h3>
                    <i className="fas fa-pie-chart"></i>
                    Asset Allocation
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Stocks (%)</label>
                      <input 
                        type="number" 
                        name="allocationStocks" 
                        value={inputs.allocationStocks} 
                        onChange={handleChange}
                        placeholder="60.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-bar"></i> ETFs (%)</label>
                      <input 
                        type="number" 
                        name="allocationETFs" 
                        value={inputs.allocationETFs} 
                        onChange={handleChange}
                        placeholder="20.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-shield"></i> Bonds (%)</label>
                      <input 
                        type="number" 
                        name="allocationBonds" 
                        value={inputs.allocationBonds} 
                        onChange={handleChange}
                        placeholder="15.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill"></i> Cash (%)</label>
                      <input 
                        type="number" 
                        name="allocationCash" 
                        value={inputs.allocationCash} 
                        onChange={handleChange}
                        placeholder="5.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-gem"></i> Gold (%)</label>
                      <input 
                        type="number" 
                        name="allocationGold" 
                        value={inputs.allocationGold} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-house"></i> Real Estate (%)</label>
                      <input 
                        type="number" 
                        name="allocationRealEstate" 
                        value={inputs.allocationRealEstate} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Dividend Yield (%)</label>
                      <input 
                        type="number" 
                        name="dividendYield" 
                        value={inputs.dividendYield} 
                        onChange={handleChange}
                        placeholder="1.50"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Inflation Rate (%)</label>
                      <input 
                        type="number" 
                        name="inflation" 
                        value={inputs.inflation} 
                        onChange={handleChange}
                        placeholder="3.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Expense Ratio (%)</label>
                      <input 
                        type="number" 
                        name="expenseRatio" 
                        value={inputs.expenseRatio} 
                        onChange={handleChange}
                        placeholder="0.50"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Tax on Gains (%)</label>
                      <input 
                        type="number" 
                        name="taxOnGains" 
                        value={inputs.taxOnGains} 
                        onChange={handleChange}
                        placeholder="15.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bullseye"></i> Goal Amount</label>
                      <input 
                        type="number" 
                        name="goalAmount" 
                        value={inputs.goalAmount} 
                        onChange={handleChange}
                        placeholder="100000.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className="input-row">
                    <div className="checkbox-field">
                      <label  className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="reinvestDividends" 
                          checked={inputs.reinvestDividends} 
                          onChange={handleChange}
                        />
                        <i className="fa-solid fa-arrows-rotate"></i>
                        Reinvest Dividends
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Risk Analysis Tab */}
          {activeTab === "risk" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chart-line"></i>
                  Risk Analysis
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-bar"></i> Market Volatility (%)</label>
                    <input 
                      type="number" 
                      name="volatility" 
                      value={inputs.volatility} 
                      onChange={handleChange}
                      placeholder="15.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrow-down"></i> Max Drawdown (%)</label>
                    <input 
                      type="number" 
                      name="maxDrawdown" 
                      value={inputs.maxDrawdown} 
                      onChange={handleChange}
                      placeholder="20.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-bolt"></i> Crash Scenario (%)</label>
                    <input 
                      type="number" 
                      name="marketCrash" 
                      value={inputs.marketCrash} 
                      onChange={handleChange}
                      placeholder="30.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-clock"></i> Recovery Time (mo)</label>
                    <input 
                      type="number" 
                      name="recoveryPeriod" 
                      value={inputs.recoveryPeriod} 
                      onChange={handleChange}
                      placeholder="24"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrows-rotate"></i> Rebalancing Frequency</label>
                    <select name="rebalancing" value={inputs.rebalancing} onChange={handleChange}>
                      <option value="none">None</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-shield"></i> Risk Tolerance</label>
                    <select name="riskTolerance" value={inputs.riskTolerance} onChange={handleChange}>
                      <option value="low">Low</option>
                      <option value="moderate">Moderate</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                <div className="input-row">
                  
                </div>
              </div>
            </div>
          )}

          {/* Retirement Planning Tab */}
          {activeTab === "retirement" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-umbrella-beach"></i>
                  Retirement Planning
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-user"></i> Current Age</label>
                    <input 
                      type="number" 
                      name="currentAge" 
                      value={inputs.currentAge} 
                      onChange={handleChange}
                      placeholder="35"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-flag"></i> Retirement Age</label>
                    <input 
                      type="number" 
                      name="retirementAge" 
                      value={inputs.retirementAge} 
                      onChange={handleChange}
                      placeholder="65"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-dollar-sign"></i> Monthly Expenses</label>
                    <input 
                      type="number" 
                      name="monthlyExpenses" 
                      value={inputs.monthlyExpenses} 
                      onChange={handleChange}
                      placeholder="3000.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Withdrawal Rate (%)</label>
                    <input 
                      type="number" 
                      name="withdrawalRate" 
                      value={inputs.withdrawalRate} 
                      onChange={handleChange}
                      placeholder="4.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-piggy-bank"></i> Emergency Fund (mo)</label>
                    <input 
                      type="number" 
                      name="emergencyFund" 
                      value={inputs.emergencyFund} 
                      onChange={handleChange}
                      placeholder="6"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.finalValue >= 0 ? 'positive' : 'negative'}`}>
                          <i className="fas fa-chart-line"></i>
                          ${calc.finalValue.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> CAGR: {calc.cagr.toFixed(2)}%</span>
                        <span><i className="fas fa-calendar"></i> Years: {calc.years}</span>
                        <span><i className="fas fa-bullseye"></i> Goal: {calc.goalPct ? calc.goalPct.toFixed(2) + '%' : 'N/A'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "risk" || activeTab === "retirement") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-line"></i>
                    Portfolio Growth Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${result.finalValue.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Final Portfolio Value
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.totalInvested.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-piggy-bank"></i>
                          Total Invested
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.cagr.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          CAGR
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.maxDrawdown.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-arrow-down"></i>
                          Max Drawdown
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Total Gain:</span>
                        <span>${result.gain.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-inflation"></i> Inflation Adjusted Value:</span>
                        <span>${result.inflationAdjusted.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Total Dividends:</span>
                        <span>${result.totalDividends.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calendar"></i> Investment Period:</span>
                        <span>{result.years} years</span>
                      </div>
                      {result.goalPct && (
                        <div className="detail-row">
                          <span><i className="fas fa-bullseye"></i> Goal Achievement:</span>
                          <span>{result.goalPct.toFixed(2)}%</span>
                        </div>
                      )}
                    </div>

                    {/* Risk Metrics */}
                    <div className="risk-summary">
                      <h4>Risk Metrics</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value">
                            {riskMetrics.portfolioVolatility.toFixed(2)}%
                          </div>
                          <div className="risk-label">Portfolio Volatility</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {riskMetrics.sharpeRatio.toFixed(2)}
                          </div>
                          <div className="risk-label">Sharpe Ratio</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {riskMetrics.valueAtRisk.toFixed(2)}%
                          </div>
                          <div className="risk-label">Value at Risk (95%)</div>
                        </div>
                      </div>
                    </div>

                    {/* Charts */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-line"></i>
                        Portfolio Growth Projection
                      </h3>
                      <Line 
                        data={growthChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div><br/>

                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Asset Allocation
                      </h3>
                      <Doughnut 
                        data={allocationChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `${context.label}: ${context.parsed}%`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your portfolio details to see analysis</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "risk" && riskMetrics && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-chart-line"></i>
                  Risk Analysis
                </h3>
                <div className="risk-visualization">
                  <div className="risk-meter">
                    <div className="meter-label">Portfolio Risk Score</div>
                    <div className="meter-bar">
                      <div 
                        className="meter-fill"
                        style={{ width: `${Math.min(riskMetrics.riskScore, 100)}%` }}
                      ></div>
                    </div>
                    <div className="meter-value">{riskMetrics.riskScore.toFixed(2)}%</div>
                  </div>
                  
                  <div className="risk-breakdown">
                    <h4>Risk Metrics</h4>
                    <div className="breakdown-item">
                      <span>Portfolio Volatility:</span>
                      <span>{riskMetrics.portfolioVolatility.toFixed(2)}%</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Sharpe Ratio:</span>
                      <span>{riskMetrics.sharpeRatio.toFixed(2)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Value at Risk (95%):</span>
                      <span>{riskMetrics.valueAtRisk.toFixed(2)}%</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Max Drawdown:</span>
                      <span>{inputs.maxDrawdown}%</span>
                    </div>
                  </div>

                  <div className="position-breakdown">
                    <h4>Scenario Analysis</h4>
                    <div className="breakdown-item">
                      <span>Market Crash Impact:</span>
                      <span>-{inputs.marketCrash}%</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Recovery Period:</span>
                      <span>{inputs.recoveryPeriod} months</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Risk Tolerance:</span>
                      <span className={`risk-level ${inputs.riskTolerance}`}>
                        {inputs.riskTolerance}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "retirement" && retirementReadiness && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-umbrella-beach"></i>
                  Retirement Readiness
                </h3>
                <div className="retirement-analysis">
                  <div className="results-grid">
                    <div className="result-card highlight">
                      <div className="result-value">
                        {retirementReadiness.readinessScore.toFixed(2)}%
                      </div>
                      <div className="result-label">
                        Retirement Readiness Score
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${retirementReadiness.retirementCorpus.toLocaleString()}
                      </div>
                      <div className="result-label">
                        Required Retirement Corpus
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        {retirementReadiness.yearsToRetirement}
                      </div>
                      <div className="result-label">
                        Years to Retirement
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${retirementReadiness.emergencyFundRequired.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Emergency Fund Required
                      </div>
                    </div>
                  </div>

                  <div className="position-breakdown">
                    <h4>Retirement Income Projection</h4>
                    <div className="income-breakdown">
                      <div className="breakdown-item">
                        <span>Monthly Retirement Income:</span>
                        <span>${result?.monthlyRetirementIncome.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Annual Retirement Income:</span>
                        <span>${((result?.monthlyRetirementIncome || 0) * 12).toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Withdrawal Rate:</span>
                        <span>{inputs.withdrawalRate}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default StockPortfolioCalculator;